import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandM,
  BrandS,
  BrandXS,
  BrandXXL,
} from '@sorare/core/src/atoms/typography';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { OnboardingButton } from '@sorare/core/src/components/onboarding/OnboardingButton';
import { FOOTBALL_ONBOARDING_TUTORIAL_RECRUIT } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MarketGuidanceLayout } from '#src/components/onboarding/MarketGuidanceLayout/index.tsx';
import { ConversionCredit } from '#src/pages/onboarding_/tutorial/ConversionCredit/index.tsx';

import bezelBg from './assets/bezelBg.webp';

const ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY = 200000;

const Root = styled(Vertical).attrs({ gap: 2 })`
  justify-content: space-between;
  flex: 1;
  width: 100%;

  @media ${tabletAndAbove} {
    width: 100%;
  }
`;

const StyledBezel = styled(Bezel)`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--double-unit);
  max-width: 500px;
  margin: 0 auto;
`;

const StyledOnboardingButton = styled(OnboardingButton)`
  position: sticky;
  bottom: var(--double-unit);
  align-self: center;
`;

const Bottom = styled(Vertical)`
  align-self: flex-end;
`;

const TutorialIntroPage = () => {
  const { fiatCurrency } = useCurrentUserCurrencies();

  return (
    <MarketGuidanceLayout progress={1} center ctaBackgroundColor="#0c0524">
      <Root grow>
        <Vertical gap={1.5} center>
          <SorareLogo />
          <BrandM className="text-center">
            <FormattedMessage defaultMessage="You're invited to play Sorare" />
          </BrandM>
        </Vertical>
        <StyledBezel
          background={`url(${bezelBg}) no-repeat center center / cover`}
        >
          <Bottom center gap={0.5}>
            <BrandXS color="var(--c-nd-600)">
              <FormattedMessage defaultMessage="Prize pool" />
            </BrandXS>
            <BrandXXL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color': 'var(--c-gradient-gold)',
                } as CSSProperties
              }
            >
              <FormattedNumber
                style="currency"
                currency={fiatCurrency}
                value={ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY}
                currencyDisplay="narrowSymbol"
                notation="compact"
                maximumFractionDigits={0}
              />
              *
            </BrandXXL>
            <BrandS>
              <FormattedMessage defaultMessage="each GameWeek" />
            </BrandS>
          </Bottom>
        </StyledBezel>
        <Vertical gap={0}>
          <ConversionCredit />
          <StyledOnboardingButton
            size="large"
            color="secondary"
            to={FOOTBALL_ONBOARDING_TUTORIAL_RECRUIT}
            prefetch="render"
          >
            <FormattedMessage {...glossary.continue} />
          </StyledOnboardingButton>
        </Vertical>
      </Root>
    </MarketGuidanceLayout>
  );
};
export default TutorialIntroPage;
